import { useEffect, useMemo, useState, useId } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { fmtDistance, fmtDurationShort, fmtElevation } from "../lib/format";
import { CHART } from "../lib/designTokens";
import { visualForTimelineMetric } from "../lib/metricVisuals";
import { cn } from "../lib/utils";
import {
  METRICS,
  buildYearWindows,
  buildMonthlyBuckets,
  buildWeeklyBuckets,
  findDefaultBucket,
  activityInWindow,
  getMetricValue,
  formatMetricAxis,
} from "../lib/timeline";

const CHART_METRIC_KEYS = ["distance", "time", "elevation"];

const TooltipBox = ({ active, payload, metricKey, unitSystem }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const visual = visualForTimelineMetric(metricKey);

  return (
    <div
      className="app-card p-3 font-mono text-xs shadow-app-md min-w-[10rem]"
      style={{ borderLeft: `3px solid ${visual.accent}` }}
    >
      <div className="card-label mb-1">{row.rangeLabel || row.label}</div>
      <div className="text-base font-semibold" style={{ color: visual.accent }}>
        {metricKey === "distance" && fmtDistance(row.distance_m, unitSystem)}
        {metricKey === "time" && fmtDurationShort(row.duration_s)}
        {metricKey === "elevation" && fmtElevation(row.elevation_m)}
        {metricKey !== "distance" && metricKey !== "time" && metricKey !== "elevation" && (
          <>
            {getMetricValue(row, metricKey, unitSystem).toFixed(1)}{" "}
            <span className="text-dark-text-muted">{METRICS[metricKey].unit}</span>
          </>
        )}
      </div>
      <div className="meta-text mt-1">{row.count} activit{row.count === 1 ? "y" : "ies"}</div>
    </div>
  );
};

export const ActivityTimeline = ({ activities, onBucketChange, unitSystem = "metric" }) => {
  const gradientId = useId().replace(/:/g, "");
  const windows = useMemo(() => buildYearWindows(activities), [activities]);
  const [windowId, setWindowId] = useState(null);
  const [granularity, setGranularity] = useState("monthly");
  const [selectedKey, setSelectedKey] = useState(null);
  const [metricKey, setMetricKey] = useState("distance");

  const activeWindow = useMemo(() => {
    if (!windows.length) return null;
    return windows.find((w) => w.id === windowId) || windows[windows.length - 1];
  }, [windows, windowId]);

  useEffect(() => {
    if (windows.length && !windowId) {
      setWindowId(windows[windows.length - 1].id);
    }
  }, [windows, windowId]);

  useEffect(() => {
    setSelectedKey(null);
  }, [granularity, windowId]);

  const windowActivities = useMemo(() => {
    if (!activeWindow) return activities;
    return activities.filter((a) =>
      activityInWindow(a, activeWindow.start, activeWindow.end)
    );
  }, [activities, activeWindow]);

  const buckets = useMemo(() => {
    if (!activeWindow) return [];
    if (granularity === "weekly") {
      return buildWeeklyBuckets(activeWindow.start, activeWindow.end, windowActivities);
    }
    return buildMonthlyBuckets(activeWindow.start, activeWindow.end, activities);
  }, [activeWindow, activities, granularity, windowActivities]);

  const defaultBucketKey = useMemo(
    () => findDefaultBucket(buckets)?.key ?? null,
    [buckets]
  );

  const effectiveKey = useMemo(() => {
    if (selectedKey && buckets.some((b) => b.key === selectedKey)) return selectedKey;
    return defaultBucketKey;
  }, [selectedKey, buckets, defaultBucketKey]);

  const selectedBucket = useMemo(
    () => buckets.find((b) => b.key === effectiveKey) || null,
    [buckets, effectiveKey]
  );

  useEffect(() => {
    if (effectiveKey && effectiveKey !== selectedKey) {
      setSelectedKey(effectiveKey);
    }
  }, [effectiveKey, selectedKey]);

  useEffect(() => {
    onBucketChange?.(selectedBucket);
  }, [selectedBucket, onBucketChange]);

  const chartData = useMemo(
    () =>
      buckets.map((b) => ({
        ...b,
        value: getMetricValue(b, metricKey, unitSystem),
      })),
    [buckets, metricKey, unitSystem]
  );

  const maxValue = Math.max(...chartData.map((d) => d.value), 1);
  const visual = visualForTimelineMetric(metricKey);
  const rangeOptions = useMemo(() => [...windows].reverse(), [windows]);

  if (!activities.length || !activeWindow) return null;

  const summarySource = selectedBucket || {
    distance_m: windowActivities.reduce((s, a) => s + (a.total_distance_m || 0), 0),
    duration_s: windowActivities.reduce((s, a) => s + (a.duration_seconds || 0), 0),
    elevation_m: windowActivities.reduce((s, a) => s + (a.elevation_gain_m || 0), 0),
  };

  return (
    <section className="mb-10 app-card overflow-hidden" data-testid="activity-timeline">
      <div className="p-5 sm:p-6 border-b border-dark-border flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <p className="page-eyebrow mb-1">Activity volume</p>
          <h2
            className="section-title"
            data-testid="timeline-period-label"
          >
            Activities for {selectedBucket?.rangeLabel ?? "—"}
          </h2>
          <p className="section-description">
            {selectedBucket?.count ?? 0} recorded {(selectedBucket?.count ?? 0) === 1 ? "activity" : "activities"} in the selected period.
          </p>
        </div>

        <Select value={activeWindow.id} onValueChange={setWindowId}>
          <SelectTrigger
            className="w-full sm:w-[280px] shrink-0 text-xs tracking-wide"
            data-testid="timeline-range-select"
            aria-label="Date range"
          >
            <SelectValue placeholder="Select year range" />
          </SelectTrigger>
          <SelectContent
            className="bg-dark-surface-1 border-dark-border max-h-[280px] overflow-y-auto"
            position="popper"
          >
            {rangeOptions.map((w) => (
              <SelectItem key={w.id} value={w.id} className="text-xs">
                {w.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 border-b border-dark-border">
        {CHART_METRIC_KEYS.map((key) => {
          const itemVisual = visualForTimelineMetric(key);
          const selected = metricKey === key;
          const value =
            key === "distance"
              ? fmtDistance(summarySource.distance_m, unitSystem)
              : key === "time"
                ? fmtDurationShort(summarySource.duration_s)
                : fmtElevation(summarySource.elevation_m);
          return (
            <button
              key={key}
              type="button"
              onClick={() => setMetricKey(key)}
              className={cn(
                "relative p-4 sm:p-5 text-left sm:text-center transition-colors border-b sm:border-b-0 sm:border-r last:border-b-0 last:border-r-0 border-dark-border",
                selected ? "bg-[var(--surface-secondary)]" : "hover:bg-[var(--surface-hover)]"
              )}
              aria-pressed={selected}
              data-testid={`timeline-metric-${key}`}
            >
              {selected ? (
                <span
                  className="absolute inset-x-0 bottom-0 h-[3px]"
                  style={{ background: itemVisual.accent }}
                  aria-hidden="true"
                />
              ) : null}
              <div
                className="font-mono text-2xl"
                style={{ color: selected ? itemVisual.accent : "var(--text-primary)" }}
              >
                {value}
              </div>
              <div
                className="mt-1 text-[11px] font-semibold uppercase tracking-wider"
                style={{ color: selected ? itemVisual.accent : "var(--text-secondary)" }}
              >
                {METRICS[key].label}
              </div>
            </button>
          );
        })}
      </div>

      <div className="p-4 sm:p-6 pt-4">
        <div className="h-64 sm:h-72 min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 12, right: 4, left: 0, bottom: 4 }}
              barCategoryGap="14%"
              accessibilityLayer={false}
            >
              <CartesianGrid stroke="var(--app-grid-line)" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "var(--app-text-secondary)", fontFamily: "JetBrains Mono" }}
                tickLine={false}
                axisLine={{ stroke: CHART.axis }}
                interval={granularity === "weekly" ? "preserveStartEnd" : 0}
              />
              <YAxis
                tickFormatter={(v) => formatMetricAxis(v, metricKey, unitSystem)}
                tick={{ fontSize: 11, fill: "var(--app-text-secondary)", fontFamily: "JetBrains Mono" }}
                tickLine={false}
                axisLine={{ stroke: CHART.axis }}
                domain={[0, Math.ceil(maxValue * 1.15)]}
                width={48}
              />
              <Tooltip
                content={<TooltipBox metricKey={metricKey} unitSystem={unitSystem} />}
                cursor={{ fill: CHART.cursorFill }}
              />
              <defs>
                <linearGradient id={`barPartial-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={visual.accent} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={visual.accent} stopOpacity={0.35} />
                </linearGradient>
                <linearGradient id={`barSelectedPartial-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={visual.accent} stopOpacity={1} />
                  <stop offset="100%" stopColor={visual.accent} stopOpacity={0.5} />
                </linearGradient>
              </defs>
              <Bar
                dataKey="value"
                radius={[3, 3, 0, 0]}
                maxBarSize={granularity === "weekly" ? 18 : 48}
                style={{ cursor: "pointer" }}
              >
                {chartData.map((entry) => {
                  const selected = entry.key === effectiveKey;
                  const fill = entry.isPartial
                    ? selected
                      ? `url(#barSelectedPartial-${gradientId})`
                      : `url(#barPartial-${gradientId})`
                    : visual.accent;
                  const opacity = selected ? 1 : entry.count === 0 ? 0.22 : 0.85;
                  return (
                    <Cell
                      key={entry.key}
                      fill={fill}
                      fillOpacity={entry.isPartial ? undefined : opacity}
                      stroke={selected ? CHART.hoverLine : "transparent"}
                      strokeWidth={selected ? 1 : 0}
                      onClick={() => setSelectedKey(entry.key)}
                      style={{ cursor: "pointer" }}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-4 mt-4">
          <ToggleGroup
            type="single"
            value={granularity}
            onValueChange={(v) => {
              if (!v || v === granularity) return;
              setGranularity(v);
            }}
            className="app-segment"
            aria-label="Timeline granularity"
          >
            <ToggleGroupItem
              value="weekly"
              className="app-segment-item rounded-md text-[10px] tracking-[0.2em] uppercase px-4 py-2 data-[state=on]:text-brand-primary data-[state=on]:bg-[var(--surface-primary)] data-[state=on]:shadow-none"
              data-testid="timeline-granularity-weekly"
            >
              Weekly
            </ToggleGroupItem>
            <ToggleGroupItem
              value="monthly"
              className="app-segment-item rounded-md text-[10px] tracking-[0.2em] uppercase px-4 py-2 data-[state=on]:text-brand-primary data-[state=on]:bg-[var(--surface-primary)] data-[state=on]:shadow-none"
              data-testid="timeline-granularity-monthly"
            >
              Monthly
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>
    </section>
  );
};
